import React, { useEffect, useState, useMemo, useCallback } from 'react';
import BootstrapTable from 'react-bootstrap-table-next';
import paginationFactory from 'react-bootstrap-table2-paginator';
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from 'react-icons/md';
import { FiChevronRight, FiActivity, FiCheckCircle, FiXCircle, FiAlertTriangle, FiList, FiSearch, FiX } from 'react-icons/fi';
import AdminNavbar from '../../components/CommonComponents/AdminNavbar';
import NoRecordTable from '../../components/CommonComponents/NoRecordTable';
import Defaultloadinggif from '../../layout/Others/Defaultloadinggif';
import { useMyContext } from '../../components/Provider/CommonProvider';
import { toast } from 'react-toastify';
import './PlayoutLogs.css';
import { FaEye } from 'react-icons/fa';
import MessageBox from '../../components/CommonComponents/MessageBox';

const PlayoutLogs = () => {
    const baseURL = process.env.REACT_APP_Baseurl;
    const { fetchRequest } = useMyContext();

    const [logs, setLogs] = useState([]);
    const [loading, setLoading] = useState(true);
    const [currentPage, setCurrentPage] = useState(1);
    const [pageSize, setPageSize] = useState(10);
    const [statusFilter, setStatusFilter] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [selectedLog, setSelectedLog] = useState(null);

    // ─── Data Fetching ────────────────────────────────────────
    const fetchLogs = useCallback(async () => {
        setLoading(true);
        try {
            const response = await fetchRequest({
                method: 'GET',
                url: `${baseURL}/admin/playout/logs`,
                data: {}
            });
            if (response?.status) {
                setLogs(response?.data || []);
            }
        } catch (error) {
            toast.error(error?.response?.message || 'Failed to fetch playout logs');
        } finally {
            setLoading(false);
        }
    }, [baseURL, fetchRequest]);

    useEffect(() => {
        fetchLogs();
    }, []);

    // Close drawer on Escape key
    useEffect(() => {
        const handleEsc = (e) => {
            if (e.key === 'Escape') setSelectedLog(null);
        };
        document.addEventListener('keydown', handleEsc);
        return () => document.removeEventListener('keydown', handleEsc);
    }, []);

    // ─── Computed Stats ───────────────────────────────────────
    const stats = useMemo(() => {
        const total = logs.length;
        const running = logs.filter(l => l.status === 'running').length;
        const completed = logs.filter(l => l.status === 'completed').length;
        const failed = logs.filter(l => l.status === 'failed').length;
        const killed = logs.filter(l => l.status === 'killed').length;
        return { total, running, completed, failed, killed };
    }, [logs]);

    // ─── Filtering ────────────────────────────────────────────
    const filteredData = useMemo(() => {
        let result = [...logs];
        if (statusFilter) {
            result = result.filter(item => item.status === statusFilter);
        }
        if (searchTerm) {
            const term = searchTerm.toLowerCase();
            result = result.filter(item =>
                (item.playout_name && item.playout_name.toLowerCase().includes(term)) ||
                (item.stream_key && item.stream_key.toLowerCase().includes(term)) ||
                (item.input_url && item.input_url.toLowerCase().includes(term)) ||
                (item.exit_reason && item.exit_reason.toLowerCase().includes(term)) ||
                (item.failure_message && item.failure_message.toLowerCase().includes(term)) ||
                (item.failure_type && item.failure_type.toLowerCase().includes(term))
            );
        }
        return result;
    }, [logs, statusFilter, searchTerm]);

    // ─── Helper: Relative Time ────────────────────────────────
    const timeAgo = (timestamp) => {
        if (!timestamp) return '';
        const diff = Date.now() - new Date(timestamp).getTime();
        const mins = Math.floor(diff / 60000);
        if (mins < 1) return 'just now';
        if (mins < 60) return `${mins}m ago`;
        const hrs = Math.floor(mins / 60);
        if (hrs < 24) return `${hrs}h ago`;
        const days = Math.floor(hrs / 24);
        if (days < 7) return `${days}d ago`;
        return `${Math.floor(days / 7)}w ago`;
    };

    // ─── Helper: Format Date ──────────────────────────────────
    const formatDate = (timestamp) => {
        if (!timestamp) return <span className="theme-text-color" style={{ opacity: 0.35 }}>—</span>;
        const date = new Date(timestamp);
        const dateStr = date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
        const timeStr = date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: true });

        return (
            <div className="plog-date-cell">
                <div className="date-main theme-text-color">{dateStr}</div>
                <div className="date-sub theme-text-color">{timeStr} · {timeAgo(timestamp)}</div>
            </div>
        );
    };

    const formatDateText = (timestamp) => {
        if (!timestamp) return '—';
        const date = new Date(timestamp);
        return `${date.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' })} ${date.toLocaleTimeString('en-US', { hour: 'numeric', minute: 'numeric', second: 'numeric', hour12: true })}`;
    };

    // ─── Helper: Compute Duration ─────────────────────────────
    const computeDuration = (start, end) => {
        if (!start) return null;
        const startMs = new Date(start).getTime();
        const endMs = end ? new Date(end).getTime() : Date.now();
        const totalSec = Math.max(0, Math.floor((endMs - startMs) / 1000));
        const hours = Math.floor(totalSec / 3600);
        const minutes = Math.floor((totalSec % 3600) / 60);
        const seconds = totalSec % 60;
        return {
            text: `${hours > 0 ? hours + 'h ' : ''}${minutes}m ${seconds}s`,
            seconds: totalSec
        };
    };

    // ─── Helper: Status Badge ─────────────────────────────────
    const getStatusBadge = (status) => (
        <span className={`plog-status status-${status}`}>
            <span className="status-dot"></span>
            {status}
        </span>
    );

    // ─── Helper: Exit Code ────────────────────────────────────
    const getExitCode = (code) => {
        if (code === null || code === undefined) return <span className="theme-text-color" style={{ opacity: 0.35 }}>—</span>;
        const cls = code === 0 ? 'exit-ok' : 'exit-err';
        return <span className={`plog-exit-code ${cls}`}>{code}</span>;
    };

    // ─── Table Columns ────────────────────────────────────────
    const columns = [
        {
            dataField: 'index',
            text: 'S.No',
            headerStyle: { width: '55px' },
            formatter: (cell, row, rowIndex) => (
                <span className="theme-text-color" style={{ fontWeight: 500, fontSize: '13px' }}>
                    {(currentPage - 1) * pageSize + rowIndex + 1}
                </span>
            ),
        },
        {
            dataField: 'playout_name',
            text: 'Playout',
            formatter: (cell) => <span className="plog-playout-name theme-text-color">{cell || '—'}</span>,
        },
        {
            dataField: 'stream_key',
            text: 'Stream Key',
            formatter: (cell) => cell ? <span className="plog-stream-key theme-text-color">{cell}</span> : <span className="theme-text-color" style={{ opacity: 0.35 }}>—</span>,
        },
        {
            dataField: 'status',
            text: 'Status',
            headerStyle: { width: '140px' },
            formatter: (cell) => getStatusBadge(cell),
        },
        {
            dataField: 'actual_start_time',
            text: 'Started At',
            formatter: (cell) => formatDate(cell),
        },
        {
            dataField: 'duration',
            text: 'Duration',
            isDummyField: true,
            formatter: (cell, row) => {
                const d = computeDuration(row.actual_start_time, row.actual_end_time);
                if (!d) return <span className="theme-text-color" style={{ opacity: 0.35 }}>—</span>;
                const pct = Math.min(100, (d.seconds / 7200) * 100);
                return (
                    <div className="plog-duration-cell">
                        <span className="plog-duration-text theme-text-color">{d.text}</span>
                        <div className="plog-duration-bar">
                            <div className={`plog-duration-bar-fill ${row.status}`} style={{ width: `${pct}%` }} />
                        </div>
                    </div>
                );
            },
        },
        // {
        //     dataField: 'exit_code',
        //     text: 'Exit',
        //     headerStyle: { width: '65px' },
        //     formatter: (cell) => getExitCode(cell),
        // },
        {
            dataField: 'actions',
            text: 'Details',
            isDummyField: true,
            headerStyle: { width: '100px' },
            formatter: (cell, row) => (
                <span className="theme-text-color" onClick={(e) => { e.stopPropagation(); setSelectedLog(row); }}>
                    <FaEye className='ms-2' styled={{ width: "20px", height: "20px" }} />
                    <MessageBox text="View" classname='ms-2 theme-text-color'></MessageBox>
                </span>
            ),
        },
    ];

    // ─── Pagination Options ───────────────────────────────────
    const paginationOptions = {
        totalSize: filteredData.length,
        page: currentPage,
        onPageChange: (page) => setCurrentPage(page),
        onSizePerPageChange: (sizePerPage, page) => {
            setPageSize(sizePerPage);
            setCurrentPage(page);
        },
        pageStartIndex: 1,
        paginationSize: 3,
        sizePerPage: pageSize,
        nextPageText: 'Next',
        prePageText: 'Prev',
        firstPageText: <MdKeyboardDoubleArrowLeft />,
        lastPageText: <MdKeyboardDoubleArrowRight />,
        showTotal: true,
        alwaysShowAllBtns: true,
        sizePerPageList: [
            { text: '10', value: 10 },
            { text: '25', value: 25 },
            { text: '50', value: 50 },
            { text: 'All', value: filteredData.length || 10 },
        ]
    };

    // ─── Row click opens drawer ──────────────────────────────
    const rowEvents = {
        onClick: (e, row) => {
            if (e.target.closest('.plog-view-btn')) return;
            setSelectedLog(row);
        }
    };

    // ─── Drawer Detail Section ────────────────────────────────
    const renderDrawerContent = () => {
        if (!selectedLog) return null;
        const row = selectedLog;
        const duration = computeDuration(row.actual_start_time, row.actual_end_time);

        let streamOutputs = row.stream_outputs;
        if (typeof streamOutputs === 'string') {
            try { streamOutputs = JSON.parse(streamOutputs); } catch { /* keep string */ }
        }
        const outputsStr = typeof streamOutputs === 'object'
            ? JSON.stringify(streamOutputs, null, 2)
            : (streamOutputs || '—');

        return (
            <div className="plog-drawer-body">
                {/* Drawer Header */}
                <div className="plog-drawer-header">
                    <div className="plog-drawer-header-left">
                        <h5 className="theme-text-color mb-0">{row.playout_name || 'Playout Log'}</h5>
                        {getStatusBadge(row.status)}
                    </div>
                    <button className="plog-drawer-close theme-text-color" onClick={() => setSelectedLog(null)} title="Close (Esc)">
                        <FiX size={20} />
                    </button>
                </div>

                {/* Quick Summary */}
                <div className="plog-drawer-summary">
                    <div className="plog-drawer-summary-item">
                        <span className="plog-drawer-summary-label theme-text-color">Duration</span>
                        <span className="plog-drawer-summary-value theme-text-color">{duration ? duration.text : '—'}</span>
                    </div>
                    <div className="plog-drawer-summary-item">
                        <span className="plog-drawer-summary-label theme-text-color">Exit Code</span>
                        <span className="plog-drawer-summary-value">{getExitCode(row.exit_code)}</span>
                    </div>
                    <div className="plog-drawer-summary-item">
                        <span className="plog-drawer-summary-label theme-text-color">Created</span>
                        <span className="plog-drawer-summary-value theme-text-color">{timeAgo(row.created_at)}</span>
                    </div>
                </div>

                {/* Detail Sections */}
                <div className="plog-drawer-sections">
                    {/* Section: Stream Info */}
                    <div className="plog-drawer-section">
                        <h6 className="plog-drawer-section-title theme-text-color">Stream Information</h6>
                        <div className="plog-drawer-kv-grid">
                            <DetailRow label="Stream Key" value={row.stream_key} mono />
                            <DetailRow label="Start Time" value={formatDateText(row.actual_start_time)} />
                            <DetailRow label="End Time" value={formatDateText(row.actual_end_time)} />
                            <DetailRow label="Scheduled End" value={formatDateText(row.scheduled_end_time)} />
                            <DetailRow label="Duration" value={duration ? duration.text : '—'} />
                        </div>
                    </div>

                    {/* Section: Input URL */}
                    <div className="plog-drawer-section">
                        <h6 className="plog-drawer-section-title theme-text-color">Input URL</h6>
                        <div className="plog-drawer-code-block theme-text-color">
                            {row.input_url || '—'}
                        </div>
                    </div>

                    {/* Section: Exit Info */}
                    <div className="plog-drawer-section">
                        <h6 className="plog-drawer-section-title theme-text-color">Exit Information</h6>
                        <div className="plog-drawer-kv-grid">
                            <DetailRow label="Exit Code" value={row.exit_code !== null && row.exit_code !== undefined ? String(row.exit_code) : '—'} />
                            <DetailRow label="Exit Reason" value={row.exit_reason || '—'} />
                        </div>
                    </div>

                    {/* Section: Failure Details (only if present) */}
                    {(row.failure_type || row.failure_message) && (
                        <div className="plog-drawer-section">
                            <h6 className="plog-drawer-section-title plog-drawer-section-title-error">Failure Details</h6>
                            <div className="plog-drawer-error-block">
                                {row.failure_type && <div className="plog-drawer-error-type">{row.failure_type}</div>}
                                <div className="plog-drawer-error-message">{row.failure_message || 'No additional information'}</div>
                            </div>
                        </div>
                    )}

                    {/* Section: Stream Outputs */}
                    <div className="plog-drawer-section">
                        <h6 className="plog-drawer-section-title theme-text-color">Stream Outputs</h6>
                        <div className="plog-drawer-code-block theme-text-color">
                            {outputsStr}
                        </div>
                    </div>

                    {/* Section: Timestamps */}
                    <div className="plog-drawer-section">
                        <h6 className="plog-drawer-section-title theme-text-color">Timestamps</h6>
                        <div className="plog-drawer-kv-grid">
                            <DetailRow label="Created At" value={row.created_at ? `${formatDateText(row.created_at)} (${timeAgo(row.created_at)})` : '—'} />
                            <DetailRow label="Updated At" value={row.updated_at ? `${formatDateText(row.updated_at)} (${timeAgo(row.updated_at)})` : '—'} />
                        </div>
                    </div>
                </div>
            </div>
        );
    };

    // ─── Loading State ────────────────────────────────────────
    if (loading) {
        return <Defaultloadinggif className="heightOnLoader" />;
    }

    // ─── Render ───────────────────────────────────────────────
    return (
        <>
            {/* ── Stat Summary Cards ── */}
            <div className="playout-stats-grid">
                {[
                    { key: 'total', label: 'Total Logs', value: stats.total, icon: <FiList />, click: '' },
                    { key: 'running', label: 'Running', value: stats.running, icon: <FiActivity />, click: 'running' },
                    { key: 'completed', label: 'Completed', value: stats.completed, icon: <FiCheckCircle />, click: 'completed' },
                    { key: 'failed', label: 'Failed', value: stats.failed, icon: <FiXCircle />, click: 'failed' },
                    { key: 'killed', label: 'Killed', value: stats.killed, icon: <FiAlertTriangle />, click: 'killed' },
                ].map(card => (
                    <div
                        key={card.key}
                        className="playout-stat-card theme-bg-color rounded-3"
                        style={{ cursor: 'pointer' }}
                        onClick={() => {
                            setStatusFilter(prev => prev === card.click ? '' : card.click);
                            setCurrentPage(1);
                        }}
                    >
                        <div className={`playout-stat-icon ${card.key}`}>{card.icon}</div>
                        <div className="playout-stat-info">
                            <h5 className="theme-text-color">{card.value}</h5>
                            <span className="theme-text-color">{card.label}</span>
                        </div>
                    </div>
                ))}
            </div>

            {/* ── Main Panel ── */}
            <div className="bootstrapTable p-3 rounded-3 theme-bg-color">
                <AdminNavbar
                    heading="Playout Logs"
                    totalDataLength={filteredData.length}
                    deleteCount={0}
                    navigation="/tv-playouts"
                    deleteMethod={null}
                    navText="Back to Playouts"
                    addIconHide
                    reload
                    reloadAction={fetchLogs}
                />

                {/* ── Filter Toolbar ── */}
                <div className="playout-filter-bar theme-bg-color-secondary rounded-3">
                    <div className="filter-group">
                        <span className="filter-label theme-text-color">Status</span>
                        <select
                            className="theme-bg-color theme-text-color"
                            value={statusFilter}
                            onChange={(e) => { setStatusFilter(e.target.value); setCurrentPage(1); }}
                        >
                            <option value="">All</option>
                            <option value="running">Running</option>
                            <option value="completed">Completed</option>
                            <option value="failed">Failed</option>
                            <option value="killed">Killed</option>
                        </select>
                    </div>

                    <div className="filter-group">
                        <span className="filter-label theme-text-color"><FiSearch size={14} /></span>
                        <input
                            type="search"
                            placeholder="Search playout, stream key, URL, error..."
                            className="theme-bg-color theme-text-color"
                            value={searchTerm}
                            onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1); }}
                        />
                    </div>

                    <div className="filter-right">
                        <span className="result-count theme-text-color">
                            {filteredData.length} of {logs.length} logs
                        </span>
                    </div>
                </div>

                {/* ── Table ── */}
                <BootstrapTable
                    keyField="id"
                    data={filteredData}
                    columns={columns}
                    pagination={paginationFactory(paginationOptions)}
                    className="no-border-table theme-text-color"
                    noDataIndication={<NoRecordTable result={filteredData.length} loader={false} />}
                    hover
                    headerClasses="videoWidth"
                    rowEvents={rowEvents}
                    rowStyle={{ cursor: 'pointer' }}
                />
            </div>

            {/* ── Side Drawer (Overlay + Panel) ── */}
            {selectedLog && (
                <>
                    <div className="plog-drawer-overlay" onClick={() => setSelectedLog(null)} />
                    <div className="plog-drawer theme-bg-color">
                        {renderDrawerContent()}
                    </div>
                </>
            )}
        </>
    );
};

// ─── Small helper component for key-value rows ───────────────
const DetailRow = ({ label, value, mono }) => (
    <div className="plog-drawer-kv-row">
        <span className="plog-drawer-kv-label theme-text-color">{label}</span>
        <span className={`plog-drawer-kv-value theme-text-color ${mono ? 'mono' : ''}`}>{value || '—'}</span>
    </div>
);

export default PlayoutLogs;
